import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'

import ContentUI from '../common/ContentUI'
import ContentPingBeacon from '../common/ContentPingBeacon'
import InactiveSandboxModal from '../common/InactiveSandboxModal'
import InstructionPanel from '../common/InstructionPanel'
import WorkPanel from '../common/WorkPanel'
import {useSandboxInactivityTimeout, useTabState} from '../common/util'

import LessonInstructions from './LessonInstructions'
import LessonCompletionModal from './LessonCompletionModal'
import ProductTour from './ProductTour'
import {PRODUCT_TOUR_STAGES} from './ProductTour/data'
import {useProductTourState} from './util'

import {connect} from 'react-redux'
import * as sandbox from 'app/state/modules/sandbox'
import * as hacker from 'app/state/modules/hacker'
import * as sbproxy from 'app/state/modules/sbproxy'
import * as sbrepl from 'app/state/modules/sbrepl'
import * as progress from 'app/state/modules/progress'

function LessonUI(props) {
  // LessonUI isn't mounted until the content details have been fetched, so these values are
  // immutable for the lifetime of the component
  const { isDemo, contentId, contentDetails, sandboxTemplateName } = props
  const hasSandboxTemplate = sandboxTemplateName.length > 0
  const hasTests = contentDetails.has_test

  // We can set our desired intercept state (based on intercept_on_load) before we have a sandbox:
  // sbproxy will cache this as persistent state and act upon it when the proxy is ready to be used
  const { setInitialInterceptState } = props
  const wantsInterceptsEnabledInitially = !!contentDetails.intercept_on_load
  useEffect(() => {
    setInitialInterceptState(wantsInterceptsEnabledInitially)
  }, [wantsInterceptsEnabledInitially])

  // Figure out if the user hasn't yet completed the latest version of the product tour.
  // Currently, we only offer the product tour on SQL Injection: Part 1.
  const canOfferProductTour = contentId === 'ffaf1a2c-563c-49ea-94f9-a9be559a21b9'
  const [shouldOfferProductTour, flagProductTourCompleted] = useProductTourState()
  const [wantsProductTour, setWantsProductTour] = useState(false)

  // Kick off some state initialization now that we're ready to start this content
  const { requestNewSandbox, abandonSandbox, fetchTestSubmissions, setupProgress, teardownProgress } = props
  useEffect(() => {
    // Request a sandbox (without supplying forceNew, so we'll retain existing sandbox on reload)
    if (hasSandboxTemplate) {
      requestNewSandbox(contentId, sandboxTemplateName)
    }

    // If the lesson has tests, fetch the user's code submissions for this lesson
    if (hasTests) {
      fetchTestSubmissions(contentId)
    }

    // Initialize the progress module to notify api-hacker of the user's progress through the lesson
    setupProgress(contentId, sandboxTemplateName)

    // Clean up if we leave this lesson
    return () => {
      abandonSandbox()
      teardownProgress()
    }
  }, [])

  // Pull some state from other modules, and format data to feed down to child components
  const { hasCode, numProxyRequests, interceptedRequest, resumeInterceptedRequest } = props
  const browserTabs = contentDetails.tabs.sort((a, b) => a.sort_order - b.sort_order)
  const testStages = contentDetails.tests.map((obj) => ({
    name: obj.test_name,
    title: obj.title,
    description: obj.description,
  }))

  // Track a bit of state that affects the UI as a whole
  const proxiedBrowserTabIndex = browserTabs.findIndex((tab) => tab.proxy)
  const hasProxy = proxiedBrowserTabIndex >= 0
  const [activeTabName, onActivateTab] = useTabState('browser-0')
  const actualActiveTabName = (proxiedBrowserTabIndex >= 0 && interceptedRequest) ? `browser-${proxiedBrowserTabIndex}` : activeTabName

  // When we start running tests, switch to the tests tab as if the user had clicked on it
  const { isRunningTests } = props
  useEffect(() => {
    if (isRunningTests) {
      onActivateTab('user', 'tests')
    }
  }, [isRunningTests])

  // TEMP: Support mainTabAppNameOverride (e.g. ?app_name_override=app) during K8s rollout
  const { mainTabAppNameOverride } = props
  const actualBrowserTabs = !mainTabAppNameOverride ? browserTabs : (
    [{ ...browserTabs[0], app_name: mainTabAppNameOverride }].concat(browserTabs.slice(1))
  )

  // Notify the sbrepl module when the user opens the Code Editor tab: so that it knows it has to
  // respect the user's preferred language choice instead of falling back on Python.
  // (This is a workaround for the fact that compiled languages are slow to evaluate with repl.)
  const { noteUserHasOpenedCodeEditor } = props
  useEffect(() => {
    if (activeTabName === 'code') {
      noteUserHasOpenedCodeEditor()
    }
  }, [activeTabName])

  // When a new sandbox is requested, automatically switch to the first browser tab
  const { lastSandboxRequestTimestamp } = props
  useEffect(() => {
    if (lastSandboxRequestTimestamp) {
      onActivateTab('user', 'browser-0')
    }
  }, [lastSandboxRequestTimestamp])

  // Allow the user to try completing the lesson once they've reached the last step: the
  // LessonCompletionDialog will decide whether they still need to pass tests etc.
  const [wantsToCompleteLesson, setWantsToCompleteLesson] = useState(false)

  // If the user sits too long without interacting with the sandbox, show a modal forcing them to
  // request a new sandbox
  const { trackProgress } = props
  const sandboxIsInactive = useSandboxInactivityTimeout(120 * 60 * 1000)
  return (
    <ContentUI>
      {wantsToCompleteLesson && (
        <LessonCompletionModal
          contentId={contentId}
          hasTests={hasTests}
          onClose={() => setWantsToCompleteLesson(false)}
          flagLessonCompleted={() => trackProgress(contentDetails.steps.length)}
        />
      )}
      {sandboxIsInactive && (
        <InactiveSandboxModal
          onClose={() => requestNewSandbox(contentId, sandboxTemplateName)}
        />
      )}
      {!sandboxIsInactive && (
        <ContentPingBeacon contentId={contentId} interval={5000} />
      )}
      <InstructionPanel
        contentId={contentId}
        title={contentDetails.title}
      >
        {(scrollToTop) => (
          <LessonInstructions
            hasTests={hasTests}
            contentCompletedAt={contentDetails.completed_at}
            contentPassedAt={contentDetails.passed_at}
            title={contentDetails.title}
            steps={contentDetails.steps.sort((a, b) => a.sort_order - b.sort_order)}
            shouldOfferProductTour={canOfferProductTour && shouldOfferProductTour}
            scrollToTop={scrollToTop}
            onProductTourRequested={() => setWantsProductTour(true)}
            onAttemptCompleteLesson={() => setWantsToCompleteLesson(true)}
          />
        )}
      </InstructionPanel>
      <WorkPanel
        contentId={contentId}
        sandboxTemplateName={sandboxTemplateName}
        browserTabs={actualBrowserTabs}
        hasCode={hasCode}
        hasProxy={hasProxy}
        hasTests={hasTests}
        hasOutput={!contentDetails.hide_terminal}
        activeTabName={actualActiveTabName}
        onActiveTabNameChange={(tabName) => onActivateTab('user', tabName)}
        numProxyRequests={numProxyRequests}
        testStages={testStages}
        interceptedRequest={interceptedRequest}
        onSubmitInterceptedRequest={resumeInterceptedRequest}
      />
      {wantsProductTour && (
        <ProductTour
          stages={PRODUCT_TOUR_STAGES}
          onStageChange={(stageIndex, extra) => {
            onActivateTab('tour', extra?.lockedTabName || null)
          }}
          onDismiss={() => {
            setWantsProductTour(false)
            onActivateTab('tour', null)
          }}
          onFinish={() => {
            setWantsProductTour(false)
            onActivateTab('tour', null)
            flagProductTourCompleted()
          }}
        />
      )}
    </ContentUI>
  )
}
LessonUI.propTypes = {
  isDemo: PropTypes.bool.isRequired,
  contentId: PropTypes.string.isRequired,
  contentDetails: PropTypes.object.isRequired,
  sandboxTemplateName: PropTypes.string.isRequired,
  mainTabAppNameOverride: PropTypes.string,
  hasCode: PropTypes.bool.isRequired,
  isRunningTests: PropTypes.bool.isRequired,
  numProxyRequests: PropTypes.number.isRequired,
  interceptedRequest: PropTypes.object,
  lastSandboxRequestTimestamp: PropTypes.number,

  setInitialInterceptState: PropTypes.func.isRequired,
  requestNewSandbox: PropTypes.func.isRequired,
  abandonSandbox: PropTypes.func.isRequired,
  fetchTestSubmissions: PropTypes.func.isRequired,
  setupProgress: PropTypes.func.isRequired,
  teardownProgress: PropTypes.func.isRequired,
  trackProgress: PropTypes.func.isRequired,
  resumeInterceptedRequest: PropTypes.func.isRequired,
  noteUserHasOpenedCodeEditor: PropTypes.func.isRequired,
}

export default connect((state) => {
  const { concludedHistory, hasCompletedInitialPageLoad, requests, isIntercepting } = state.sbproxy
  const canIntercept = hasCompletedInitialPageLoad && isIntercepting && requests.length > 0
  return {
    hasCode: state.sbrepl.supportedEngines.length > 0,
    isRunningTests: state.repl.runningTests,
    numProxyRequests: concludedHistory ? Object.keys(requests).length : 0,
    interceptedRequest: canIntercept && !requests[0].response ? requests[0] : null,
    lastSandboxRequestTimestamp: state.sandbox.lastRequest ? state.sandbox.lastRequest.timestamp : null,
  }
}, (dispatch) => ({
  setInitialInterceptState: (value) => dispatch(sbproxy.setWantsToIntercept(value)),
  requestNewSandbox: (contentId, templateName) => dispatch(sandbox.requestNew(contentId, templateName, false)),
  abandonSandbox: () => dispatch(sandbox.abandon()),
  fetchTestSubmissions: (contentId) => dispatch(hacker.getCodeSubmissionsEx(contentId)),
  setupProgress: (contentId, templateName) => dispatch(progress.setup(contentId, templateName)),
  teardownProgress: () => dispatch(progress.teardown()),
  trackProgress: (stepIndex) => dispatch(progress.track(stepIndex)),
  resumeInterceptedRequest: (requestText, isEdited) => dispatch(sbproxy.resumeInterceptedRequest(requestText, isEdited)),
  noteUserHasOpenedCodeEditor: () => dispatch(sbrepl.noteUserHasOpenedCodeEditor()),
}))(LessonUI)
